import React, { useState, useEffect } from "react";
import { Loader2, ShieldCheck, BellRing } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { GRANTABLE } from "../lib/perms";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { ChannelToggles } from "./ChannelToggles";

// Reusable edit dialog for gestores and colaboradores.
export const UserEditDialog = ({ user, endpoint, open, onOpenChange, onSaved, niveis }) => {
    const { user: me } = useAuth();
    const [form, setForm] = useState({ name: "", nivel: "", phone: "", password: "" });
    const [perms, setPerms] = useState([]);
    const [notif, setNotif] = useState({});
    const [saving, setSaving] = useState(false);
    const canEditPerms = !!me?.is_master;

    useEffect(() => {
        if (user) {
            setForm({ name: user.name || "", nivel: user.nivel || "", phone: user.phone || "", password: "" });
            setPerms(user.permissions || []);
            setNotif(user.notif_prefs || {});
        }
    }, [user]);

    const togglePerm = (k, c) => setPerms((p) => (c ? Array.from(new Set([...p, k])) : p.filter((x) => x !== k)));

    const save = async () => {
        if (!form.name) { toast.error("Informe o nome"); return; }
        setSaving(true);
        try {
            const payload = { name: form.name, nivel: form.nivel, phone: form.phone };
            if (form.password) payload.password = form.password;
            if (canEditPerms) payload.permissions = perms;
            payload.notif_prefs = notif;
            const { data } = await api.put(`${endpoint}/${user.user_id}`, payload);
            toast.success("Dados atualizados");
            onSaved?.(data);
            onOpenChange(false);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    if (!user) return null;
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[90vh] overflow-y-auto rounded-2xl sm:max-w-lg" data-testid="user-edit-dialog">
                <DialogHeader><DialogTitle className="font-heading text-2xl font-medium">Editar {user.name}</DialogTitle></DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label>Nome completo</Label>
                        <Input data-testid="edit-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-xl" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Função / Nível</Label>
                            {niveis ? (
                                <select data-testid="edit-nivel" value={form.nivel} onChange={(e) => setForm({ ...form, nivel: e.target.value })} className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm">
                                    {niveis.map((n) => <option key={n} value={n}>{n}</option>)}
                                </select>
                            ) : (
                                <Input data-testid="edit-nivel" value={form.nivel} onChange={(e) => setForm({ ...form, nivel: e.target.value })} className="mt-1 rounded-xl" />
                            )}
                        </div>
                        <div>
                            <Label>Telefone</Label>
                            <Input data-testid="edit-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 rounded-xl" />
                        </div>
                    </div>
                    <div>
                        <Label>Nova senha (opcional)</Label>
                        <Input data-testid="edit-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 rounded-xl" placeholder="Deixe em branco para manter" />
                    </div>
                    {canEditPerms && (
                        <div>
                            <Label className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-accent" /> Permissões de acesso</Label>
                            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                                {GRANTABLE.map((f) => (
                                    <div key={f.key} className="flex items-center justify-between rounded-xl border border-border/70 bg-muted/30 px-3 py-2">
                                        <span className="text-sm">{f.label}</span>
                                        <Switch data-testid={`edit-perm-${f.key}`} checked={perms.includes(f.key)} onCheckedChange={(c) => togglePerm(f.key, c)} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    <div>
                        <Label className="flex items-center gap-2"><BellRing className="h-4 w-4 text-accent" /> Canais de notificação</Label>
                        <div className="mt-2"><ChannelToggles prefs={notif} onToggle={(k, v) => setNotif((n) => ({ ...n, [k]: v }))} idPrefix="edit-" /></div>
                    </div>
                    <Button onClick={save} disabled={saving} className="w-full rounded-full" data-testid="edit-save-btn">
                        {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salvar alterações
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};
